/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import * as yup from 'yup'
import { TYPE } from './EditFormConstants'

yup.setLocale({
  mixed: {
    required: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
  },
  string: {
    max: 'FORM_VALIDATION_ERROR_MAX_LENGTH_IS_${max}'
  }
})

const ValidationSchema = () => {
  return yup.object().shape({
    options: yup
      .array()
      .of(
        yup.object().shape({
          value: yup.mixed(),
          label: yup.mixed()
        })
      )
      .default(() => [])
      .nullable(),
    metadata: yup.object().shape({
      name: yup
        .string()
        .trim()
        .max(255)
        .required()
        .default(() => ''),
      description: yup
        .string()
        .max(255)
        .nullable()
        .default(() => ''),
      type: yup
        .string()
        .oneOf(Object.values(TYPE), 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
        .required()
        .default(() => ''),
      definition: yup
        .object()
        .default(() => ({}))
        .shape({
          defaultValue: yup.mixed().when('$metadata.type', (type: any, schema: any) => {
            switch (type) {
              case TYPE.DATETIME:
                return yup
                  .mixed()
                  .nullable()
                  .when('$metadata.readOnly', {
                    is: true,
                    then: (s: any) => s.required()
                  })
                  .default(() => null)
              case TYPE.STRING:
                return yup
                  .string()
                  .default(() => '')
                  .when('$metadata.readOnly', {
                    is: true,
                    then: (s: any) => s.required()
                  })
                  .nullable()
              case TYPE.SELECT_ONE:
                return yup
                  .string()
                  .when('$metadata.readOnly', {
                    is: true,
                    then: (s: any) => s.required()
                  })
                  .nullable()
                  .default(() => null)
              case TYPE.SELECT_ONE_USER:
              case TYPE.SELECT_ONE_GROUP:
                return yup
                  .number()
                  .when('$metadata.readOnly', {
                    is: true,
                    then: (s: any) => s.required()
                  })
                  .nullable()
                  .default(() => null)
              case TYPE.NUMBER:
                return yup
                  .number()
                  .max(Number.MAX_SAFE_INTEGER)
                  .when('$metadata.readOnly', {
                    is: true,
                    then: (s: any) => s.max(Number.MAX_SAFE_INTEGER).required()
                  })
                  .nullable()
                  .default(() => null)
              case TYPE.SELECT_MULTI:
              case TYPE.SELECT_MULTI_USER:
              case TYPE.SELECT_MULTI_GROUP:
                return yup
                  .mixed()
                  .nullable()
                  .default(() => null)
              default:
                return yup
                  .mixed()
                  .nullable()
                  .default(() => null)
            }
          }),
          defaultValues: yup.mixed().when('$metadata.type', (type: any, schema: any) => {
            switch (type) {
              case TYPE.SELECT_ONE:
              case TYPE.SELECT_ONE_USER:
              case TYPE.SELECT_ONE_GROUP:
              case TYPE.DATETIME:
              case TYPE.STRING:
              case TYPE.NUMBER:
                return yup
                  .mixed()
                  .nullable()
                  .default(() => null)
              case TYPE.SELECT_MULTI:
                return yup
                  .array()
                  .of(yup.string())
                  .when('$metadata.readOnly', {
                    is: true,
                    then: (s: any) => s.required()
                  })
                  .nullable()
                  .default(() => null)
              case TYPE.SELECT_MULTI_USER:
              case TYPE.SELECT_MULTI_GROUP:
                return yup
                  .array()
                  .of(yup.number())
                  .when('$metadata.readOnly', {
                    is: true,
                    then: (s: any) => s.required()
                  })
                  .nullable()
                  .default(() => null)
              default:
                return yup
                  .mixed()
                  .nullable()
                  .default(() => null)
            }
          }),
          options: yup.lazy((value: any, options: any) => {
            const type = options.context?.metadata?.type

            switch (type) {
              case TYPE.SELECT_ONE:
              case TYPE.SELECT_MULTI:
                return yup
                  .array()
                  .min(1, 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
                  .required()
                  .default(() => [])
              default:
                return yup
                  .mixed()
                  .nullable()
                  .default(() => null)
            }
          })
        }),
      readOnly: yup.boolean().default(() => false),
      id: yup.number().nullable()
    })
  })
}

export default ValidationSchema
